<template>
	<app-title-bar
		:icon="['fas', 'fa-truck']"
		subtitle="Collection"
		title="Collection"
		:badges="[collectionNumber, collection?.contract_number, collection?.account_name]">
		<div class="flex gap-3">
			<app-button to="/yard/collection-items" icon="fa-circle-left"> Back </app-button>

			<resource-details :loading="loading || !collection" :resource="collection" />

			<status-badge
				v-if="collection?.status && collection.status.length > 0"
				:status="collection.status[0]"
				status-key="collection_status" />
		</div>
	</app-title-bar>

	<div class="flex flex-col-reverse 2xl:flex-row gap-4">
		<app-panel class="grow" title="Collection Details">
			<template #header>
				<div class="flex grow-1 gap-3">
					<app-badge v-if="collection && collection.multi_journey !== 0"
						>Multi Journeys Exist</app-badge
					>

					<div class="flex gap-2">
						<operators-dropdown v-model="assignOperator" label="Assign Operators" />
						<app-button size="small" theme="outline" @click="assignAll"
							>Assign All</app-button
						>
					</div>
				</div>
			</template>

			<assign-collection-table
				:items="items"
				:loading="loading"
				@updated-items="getData"
				@select="(value: CollectionItem[]) => (selected = value)"
				@update="updated" />

			<div class="flex justify-between mt-5 gap-3">
				<app-button
					v-if="collection"
					icon="fa-note-sticky"
					:disabled="loading"
					:theme="collection?.notes ? 'danger' : 'primary'"
					@click="editing = collection">
					{{ collection?.notes ? 'Edit Collection Note' : 'Add Collection Note' }}
				</app-button>

				<div v-if="items.length > 0" class="flex justify-end gap-3">
					<app-button :disabled="!canSendToOperator" @click="send"
						>Send to Operator</app-button
					>
				</div>
			</div>
		</app-panel>
	</div>

	<add-edit-collection-note
		:collection="editing"
		@updated="(notes) => (collection!.notes = notes)"
		@closed="editing = null" />
</template>

<script lang="ts" setup>
	import { ref, computed, onMounted } from 'vue'
	import { useRoute } from 'vue-router'
	import useAuthStore from '@/stores/auth'
	import useOperatorStore from '@/stores/operators'
	import useToasterStore from '@/stores/toaster'
	import { Collection } from '@/data/schema/collection'
	import type { CollectionItem } from '@/data/schema/collection-item'
	import CollectionService from '@/services/collection'
	import AssignCollectionTable from '@/components/datatables/yard/collection/AssignCollectionTable.vue'
	import ResourceDetails from '@/components/partials/panels/ResourceDetails.vue'
	import StatusBadge from '@/components/partials/cat2/StatusBadge.vue'
	import AddEditCollectionNote from '@/components/partials/cat2/AddEditCollectionNote.vue'
	import OperatorsDropdown from '@/components/partials/dropdowns/OperatorsDropdown.vue'

	// Resources
	const api = new CollectionService()
	const toaster = useToasterStore()
	const route = useRoute()
	const authStore = useAuthStore()
	const operatorStore = useOperatorStore()

	// Data
	const collectionNumber = ref()
	const collection = ref<Collection>()
	const items = ref<CollectionItem[]>([])
	const selected = ref<CollectionItem[]>([])
	const loading = ref(true)
	const editing = ref<Collection | null>(null)
	const assignOperator = ref('')

	const canSendToOperator = computed(() => {
		return selected.value.length > 0 && selected.value.every((obj) => obj.assigned_to)
	})

	const getData = () => {
		loading.value = true

		api.get(collectionNumber.value, {
			withItems: true
		})
			.then((res) => {
				collection.value = res.data.data

				if (collection.value && collection.value.items) {
					items.value = collection.value?.items
				}

				loading.value = false
			})
			.catch((error) => {
				console.log(error)
				toaster.error({ text: 'An error occurred retrieving collection' })
				loading.value = false
			})
	}

	/**
	 * Update selected items
	 */
	function send() {
		let saveItems = []

		saveItems = selected.value.map((item) => ({
			id: item.id,
			assigned_to: item.assigned_to,
			status: 1
		}))

		toaster.working({
			text: 'Sending items to operators...',
			dismissable: false,
			timeout: 0
		})

		api.updateItems(saveItems)
			.then(() => {
				toaster.clear()
				toaster.success({
					text: 'Items sent successfully'
				})
				getData()
			})
			.catch((error) => {
				toaster.clear()
				toaster.error({
					text: 'Error occurred when assigning items'
				})
				console.error(error)
			})
	}

	/**
	 * Items have been updated
	 * Note: We are updating both the values in items and selected
	 * in order to keep in-sync with the table selected values
	 */
	function updated(item: CollectionItem, kit?: CollectionItem) {
		const index = items.value.findIndex((i) => i.id === item.id)
		const selectedIndex = selected.value.findIndex((i) => i.id == item.id)

		if (index !== -1 && !kit) {
			items.value[index].assigned_to = item.assigned_to
		}

		if (selectedIndex !== -1 && !kit) {
			selected.value[selectedIndex].assigned_to = item.assigned_to
		}

		if (index !== -1 && kit) {
			const kitIndex = items.value[index].kit_components!.findIndex((i) => i.id == kit.id)
			items.value[index].kit_components![kitIndex].assigned_to = kit.assigned_to
		}

		if (kit) {
			const selectedKitIndex = selected.value.findIndex((i) => i.id == kit.id)

			if (selectedKitIndex !== -1) {
				selected.value[selectedKitIndex].assigned_to = kit.assigned_to
			}
		}
	}

	/**
	 * Set all contract items to assignOperator value
	 */
	function assignAll() {
		items.value.forEach((item) => {
			item.assigned_to = assignOperator.value
			item.changed = true

			if (item.kit_components) {
				item.kit_components.forEach((component) => {
					component.assigned_to = assignOperator.value
					component.changed = true
				})
			}
		})
	}

	onMounted(() => {
		collectionNumber.value = route.params.id
		operatorStore.set(authStore.user!.current_depot!)

		if (collectionNumber.value) {
			getData()
		}
	})
</script>
